import { useFormBehaviors } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { FC, createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { Position, ShiftParticipationEnum } from 'types/graphql';
import { IBKJComboBoxAsyncOption } from '@bookjane2/bookjane-design-library/lib/components/BKJComboBoxAsync/BKJComboBoxAsync.types';

import {
  VALIDATION_email,
  VALIDATION_phoneNumber,
  ComboBoxOption,
  VALIDATION_position_id,
  VALIDATION_required,
  convertEmploymentStatus,
  monthToDaysMap,
  InviteTeamMemberFormApiError,
  monthNameToNumber,
  ShiftParticipationForCommunity,
} from 'components/InviteTeamMembersByCSVModal/views/InviteTeamMemberView/InviteTeamMemberView.constants';
import { METHOD_POST } from 'requests';
import { INVITE_TEAM_MEMBER } from 'constants/endpoints';
import { inviteTeaMemberCSVModalRoutingService } from 'components/InviteTeamMembersByCSVModal/InviteTeamMembersByCSVModal.ui.context';
import { TeamMemberPageContext } from 'pages/TeamMembers/Internal/TeamMemberPage.context';
import { useSelector } from 'react-redux';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import {
  DropdownItem,
  getPositionByDepartmentSelectOptions,
  mapPositionIdsByDepartment,
  UsersCommunitiesAttributes,
} from 'modals/JaneDetailsModal/views/EditJaneView/EditJaneView.constants';
import { SelectPositionByDepartmentDataType } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.types';
import { SelectInputPositionByDepartmentContext } from 'modals/JaneDetailsModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.context';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { ChangeEventType } from '@bookjane2/bookjane-design-library/lib/common.types';
import { PositionByDepartmentSelectOptionType } from 'components/PositionByDepartmentSelectInput/PositionsByDepartmentSelectInput.types';
import { useJanePositionDropdownHandlers } from 'hooks/useJanePositionDropdownHandlers';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { toastService } from 'services/toastService';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
export interface ITeamMemberViewForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isMobile: boolean;
  locale: string;
  position_id: string[];
  community_id: string;
  location: IBKJComboBoxAsyncOption;
  selectedSecondaryLocationsScheduling: string[];
  selectedSecondaryLocationsCallOut: string[];
  secondaryPositionsIds: string[];
  secondaryPositions: Position[];
  employeeStartDate: string;
  employmentStatus: string;
  hourlyRate: string;
  birthMonth: string;
  birthDay: string;
  employeeNumber: string;
}

type GroupedPositionByDepartmentSelectOptionType = PositionByDepartmentSelectOptionType & {
  groupId: string;
  isGroupNode?: boolean;
};

export type SecondaryPositionDropdownItem = DropdownItem &
  GroupedPositionByDepartmentSelectOptionType;
export interface ITeamMemberViewFormActions {
  setSelectMonth: (month: string) => void;
  setSelectDay: (day: string) => void;
  dayOptions: ComboBoxOption[];
  handlePrimaryPosition: (e: ChangeEventType) => void;
  handleSecondaryPosition: (e: ChangeEventType) => void;
  secondaryPositionsDropdownItems: SecondaryPositionDropdownItem[];
  handlePrimaryLocationChange: (e: ChangeEventType) => void;
  isDiscardConfirmationOpen: boolean;
  setIsDeleteConfirmationOpen: (modal: boolean) => void;
}

export const InviteTeamMemberViewFormContext = createContext(
  {} as UseFormBehaviors<ITeamMemberViewForm, InviteTeamMemberFormApiError> &
    ITeamMemberViewFormActions,
);

export const InviteTeamMemberViewFormProvider: FC<{}> = ({ children }) => {
  const {
    refetch: refetchData,
    values: { location },
  } = useContext(TeamMemberPageContext);
  const {
    data: newData,
    refetch: refetchPositionByDepartmentData,
  }: {
    data: SelectPositionByDepartmentDataType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    refetch: any;
  } = useContext(SelectInputPositionByDepartmentContext);
  const _options = getPositionByDepartmentSelectOptions(newData);
  const positionIdsByDepartments = mapPositionIdsByDepartment(newData);
  const isUserAgency = useSelector(isUserAgencySelector);
  const { apiRequest } = useAPIActions();
  const [selectMonth, setSelectMonth] = useState<string>('');
  const [selectDay, setSelectDay] = useState<string>('');
  const [dayOptions, setDayOptions] = useState<ComboBoxOption[]>([]);
  const filteredArray = useMemo(() => {
    return _options.filter((item) => item.key);
  }, [_options]);

  const [isDiscardConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const isMultiLocationSchedulingEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(location?.value)),
  );

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(location?.value)),
  );
  const isMultiLocationCallOutEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(location?.value)),
  );

  useEffect(() => {
    setSelectDay('');
  }, [selectMonth]);
  const memoizedRefetchData = useMemo(() => {
    return () => {
      refetchData();
    };
  }, [refetchData]);
  useMemo(() => {
    if (selectMonth) {
      // Convert month names to the expected structure with unique 'id'
      const options: ComboBoxOption[] = monthToDaysMap[selectMonth].map((day, index) => ({
        key: `${selectMonth}_${day}`,
        id: `${selectMonth}_${day}`, // Generate a unique 'id' for each day within the selected month
        value: day,
        label: day,
      }));
      setDayOptions(options);
    } else {
      setDayOptions([]);
    }
  }, [selectMonth]);

  const handleSave = useCallback(
    async (payload: ITeamMemberViewForm) => {
      const employmentStatusValue = convertEmploymentStatus(payload?.employmentStatus[0]);
      const derivedBirthMonth = monthNameToNumber[payload?.birthMonth[0]];

      const selectedSecondaryPositionsWithoutDepartments = payload.secondaryPositionsIds.filter(
        (id) => !Object.keys(positionIdsByDepartments).includes(id),
      );

      const users_communities_attributes: UsersCommunitiesAttributes[] = [];

      if (
        isMultiLocationTieringEnabled ||
        isMultiLocationSchedulingEnabled ||
        isMultiLocationCallOutEnabled
      ) {
        const addedCommunities = [
          ...(isMultiLocationSchedulingEnabled
            ? payload?.selectedSecondaryLocationsScheduling?.map((communityId) => ({
                community_id: communityId,
                shift_participation: ShiftParticipationEnum.scheduling,
              }))
            : []),
          ...(isMultiLocationTieringEnabled || isMultiLocationCallOutEnabled
            ? payload?.selectedSecondaryLocationsCallOut?.map((communityId) => ({
                community_id: communityId,
                shift_participation: ShiftParticipationEnum.callout,
              }))
            : []),
        ].reduce((acc: ShiftParticipationForCommunity[], curr) => {
          const existing = acc.find((item) => item.community_id === curr.community_id);

          if (existing) {
            existing.shift_participation = ShiftParticipationEnum.all;
          } else {
            acc.push(curr);
          }

          return acc;
        }, []);

        addedCommunities.forEach((community) => {
          users_communities_attributes.push({
            community_id: Number(community.community_id),
            shift_participation: community.shift_participation,
          });
        });
      }

      return await apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_POST,
          url: INVITE_TEAM_MEMBER(),
          body: {
            invitations: {
              first_name: payload?.firstName,
              last_name: payload?.lastName,
              email: payload?.email,
              phone: payload?.phone,
              ...(isUserAgency ? {} : { is_mobile: payload?.isMobile }),
              ...(WhiteLabelAccessor.isPetalProvider
                ? { locale: payload?.locale }
                : { locale: 'en' }),
              community_id: payload?.location?.id,
              users_communities_attributes,
              primary_position_id: payload?.position_id[0],
              secondary_position_ids: selectedSecondaryPositionsWithoutDepartments,
              ...(isUserAgency ? {} : { employment_status: employmentStatusValue }),
              ...(isUserAgency ? {} : { hourly_rate: payload?.hourlyRate }),
              employee_number: payload?.employeeNumber,
              employee_start_date: payload?.employeeStartDate[0],
              birth_day: payload?.birthDay[0],
              birth_month: derivedBirthMonth,
            },
          },
        },
        {
          onSuccess: async () => {
            memoizedRefetchData();
            inviteTeaMemberCSVModalRoutingService.goToView('InviteTeamMemberSuccessFul');
          },
          onError: (error) => {
            if (Array.isArray(error?.data)) {
              const errorMessage = error.data
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((err: any) => Object.values(err).join(', '))
                .join('. ');
              return toastService.error(errorMessage);
            }

            return handleGenericErrorMessage(error);
          },
        },
      );
    },
    [
      apiRequest,
      isMultiLocationCallOutEnabled,
      isMultiLocationSchedulingEnabled,
      isMultiLocationTieringEnabled,
      isUserAgency,
      memoizedRefetchData,
      positionIdsByDepartments,
    ],
  );
  const form = useFormBehaviors<ITeamMemberViewForm, InviteTeamMemberFormApiError>({
    isDirtyCheckEnabled: true,
    initialState: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      isMobile: false,
      locale: 'en',
      position_id: [],
      community_id: '',
      location: location,
      secondaryPositionsIds: [],
      secondaryPositions: [],
      selectedSecondaryLocationsScheduling: [],
      selectedSecondaryLocationsCallOut: [],
      employeeStartDate: '',
      employmentStatus: isUserAgency ? 'full-time' : '',
      employeeNumber: '',
      hourlyRate: '',
      birthMonth: selectMonth,
      birthDay: selectDay,
    },
    onSubmit: handleSave,
    validations: {
      firstName: VALIDATION_required,
      lastName: VALIDATION_required,
      email: VALIDATION_email,
      phone: VALIDATION_phoneNumber,
      position_id: VALIDATION_position_id,
      employmentStatus: VALIDATION_required,
    },
  });

  const handlePrimaryLocationChange = useCallback(
    async (e) => {
      await refetchPositionByDepartmentData({
        filter: { allocatedToCommunities: [Number(e.target.value?.value)] },
      });
      return form.onChange([
        e,
        {
          target: {
            name: 'position_id',
            value: [],
          },
        },
        {
          target: {
            name: 'secondaryPositionsIds',
            value: [],
          },
        },
      ]);
    },
    [form, refetchPositionByDepartmentData],
  );

  const { handlePrimaryPosition, handleSecondaryPosition } = useJanePositionDropdownHandlers({
    onChange: form.onChange,
    positionIdsByDepartments,
    primaryPositionId: form.values.position_id[0],
    secondaryPositionIds: form.values.secondaryPositionsIds,
    secondaryPositionsFormName: 'secondaryPositionsIds',
    setShowDeactivateConfirmModal: null,
  });

  const secondaryPositionsDropdownItems = useMemo(
    () =>
      filteredArray
        .filter((item) => `${item.value}` !== form.values.position_id[0])
        .filter((item) => {
          // if the department is the only node in this group, filter it out
          const isDepartmentOnlyNodeInGroup =
            item.isGroupNode &&
            positionIdsByDepartments[item.groupId]?.filter(
              (positionIdByDepartmentNode) =>
                `${positionIdByDepartmentNode}` !== `${form.values.position_id[0]}`,
            ).length < 1;

          return !isDepartmentOnlyNodeInGroup;
        })
        .map(
          (item) =>
            ({
              id: `${item?.key}`,
              value: item?.value,
              label: item?.label,
              key: `${item?.key}`,
              groupId: `${item?.groupId}`,
              isGroupNode: !!item?.isGroupNode,
            } as SecondaryPositionDropdownItem),
        ),
    [filteredArray, form.values.position_id, positionIdsByDepartments],
  );

  const value = useMemo(() => {
    return {
      ...form,
      isDisabled:
        form.isDisabled ||
        !form.values.firstName ||
        !form.values.lastName ||
        !form.values.phone ||
        !form.values.position_id?.length ||
        (!form.values.employmentStatus && !isUserAgency),
      handlePrimaryLocationChange,
      handlePrimaryPosition,
      handleSecondaryPosition,
      setSelectMonth,
      dayOptions,
      setSelectDay,
      secondaryPositionsDropdownItems,
      setIsDeleteConfirmationOpen,
      isDiscardConfirmationOpen,
    };
  }, [
    form,
    isUserAgency,
    handlePrimaryLocationChange,
    handlePrimaryPosition,
    handleSecondaryPosition,
    dayOptions,
    secondaryPositionsDropdownItems,
    isDiscardConfirmationOpen,
  ]);

  return (
    <InviteTeamMemberViewFormContext.Provider value={value}>
      {children}
    </InviteTeamMemberViewFormContext.Provider>
  );
};

export const useInviteTeamMemberViewFormContext = () => useContext(InviteTeamMemberViewFormContext);
